import { Col, Image, Input as AntInput, Tooltip } from 'antd';
import { InputProps } from 'antd/es/input';
import { CloseOutlined } from '@ant-design/icons';
import { useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import Button from '../Button';
import { useTranslation } from 'react-i18next';
import { messages } from 'app/modules/UserManagementModule/messages';
import InfoCircleIcon from '../Icons/InfoCircleIcon';
import { useHover } from 'hooks';
import HelpInfo from './HelpInfo';

interface Props extends InputProps {
  control: any;
  inputName: string;
  placeholder?: string;
  defaultValue?: string | number;
  error: any;
  helpInfo?: string[];
  callback?: (val: any) => void;
}

const CrossIcon = () => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 10 10"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M8.5294 8.52953L1.47058 1.4707M8.5294 1.4707L1.47058 8.52953"
      stroke="white"
      strokeLinecap="round"
    />
  </svg>
);

export default function FileUpload({
  control,
  inputName,
  placeholder,
  defaultValue,
  error,
  callback = () => {},
  helpInfo = [],
  ...props
}: Props) {
  const { t } = useTranslation();
  const hoverRef = useRef(null);
  const isHover = useHover(hoverRef);
  const [url, setUrl] = useState<any>('');
  const ref: any = useRef();
  const handleFileChange = file => {
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  return (
    <Controller
      control={control}
      name={inputName}
      defaultValue={defaultValue}
      render={({ field: { onChange, value, name } }) => {
        if (value === undefined && url) {
          onChange('');
          setUrl('');
        }
        if (typeof value === 'string' && value?.length !== 0) setUrl(value);
        return (
          <>
            <div className="custom-input file-input">
              <input
                style={{ display: 'none' }}
                type="file"
                onChange={(v: any) => {
                  if (v?.target?.files[0]) {
                    const files = v?.target?.files[0];
                    v.target.value = '';
                    onChange(files);
                    handleFileChange(files);
                    callback(files);
                  }
                }}
                name={name}
                ref={ref}
                accept={'image/jpg,image/jpeg,image/gif,image/png'}
              />
              <AntInput
                type="input"
                value={value?.name}
                className={
                  error?.message ? 'error-input file-input' : 'file-input'
                }
                onClick={() => {
                  ref.current.click();
                }}
                readOnly
                placeholder={placeholder}
                suffix={
                  <>
                    <Tooltip
                      title={
                        error?.message ? (
                          <span
                            dangerouslySetInnerHTML={{
                              __html: t(error?.message),
                            }}
                          />
                        ) : (
                          <HelpInfo helpInfo={helpInfo} />
                        )
                      }
                      overlayClassName={
                        error?.message ? 'error-tooltip' : 'help-tooltip'
                      }
                      placement="topRight"
                      color={''}
                      arrowPointAtCenter
                      trigger={
                        window.matchMedia('(any-pointer: coarse)').matches
                          ? 'click'
                          : 'hover'
                      }
                    >
                      {/* <img
                        src={InfoCircle}
                        alt="error icon"
                        className={error?.message ? '' : 'hidden'}
                        style={{ marginRight: '7px' }}
                      /> */}
                      <div style={{ display: 'flex' }} ref={hoverRef}>
                        {error?.message ? (
                          <InfoCircleIcon />
                        ) : helpInfo.length > 0 ? (
                          <InfoCircleIcon type="help" isHover={isHover} />
                        ) : null}
                      </div>
                    </Tooltip>
                    <Button
                      label={t(...messages.browseFiles())}
                      onClick={() => {
                        ref.current.click();
                      }}
                      type="button"
                      variation="file-upload"
                      disabled={props?.disabled}
                      style={{}}
                    />
                  </>
                }
                {...props}
              />
            </div>
            <Col span={24}>
              <div className="image-preview-container">
                {!props?.disabled && url ? (
                  <span
                    className={'close-container'}
                    onClick={() => {
                      setUrl('');
                      onChange(null);
                    }}
                  >
                    <CrossIcon />
                  </span>
                ) : null}

                <Image
                  preview={true}
                  width={80}
                  height={80}
                  // src={url && value ? url : ''}
                  style={{ objectFit: 'cover' }}
                  src={url}
                  fallback={
                    'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAFAAAABQCAYAAACOEfKtAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAARXSURBVHgB7Z3LSiNBFIZPOlFRIkYFRUEQREFwMfMEo2+SjWtfRddu+k1G38CF4HIiioKiRhRl1JCpv5gawpBI1+Wc6sT6oO0YxU59OXXpqlOxQn9pNpuN8fHxvW63u62+3aZEP07UcXB4eJibJyr4ouStjo2N/VQPVylRhNb7+/tOnuctLXB3d/cXJXm2QOL3qpLXVN80KWFLo1ar/c7Ugz1KOKH6ix8Q+I0SrmxnlPAiCfSkRiWh0+nQ3d0dvby80OvrK729vennQLVaJTVG1Uej0aDp6Wn9uAxEF/j8/ExXV1f6PAiIhFQcj4+P+rmZmRlaXFyker1OMYkmEBHWarU+FfcZEIljbm6OlpeXo0VkFIGoqpeXl/+qqA/39/f6TVhaWqL5+XmSRlzg9fW1PkKCaD4/P9dniJREtBfmkCf59/shJrDdbosUDtfAtaQQEYiqhTZPClTnEO1rEUQEYpgCiVJA3sXFBUnALhDi0FNKg2s+PT0RN+wCEX2xMINuTtgFShRiEBhvcreFrALRG0o15v3AtXFvzQmrQNfbtJBw1wBWgdzvfhG4e39WgTGrr2Goq3AZBHKTZqQ9YRWImeTYTExMECesAqempig23G8iq8DJyUmKDfdrGHmBWIDihFUgXnzMdhDrJNyLTuy98MLCAsWCO/qAiMBYUSixPsIuEPJiRCHkSSx1igykIVCyQ4E4qdU5EYGIwrW1NZGqjGtsbGyQFGK3coiK9fV1VolGnmSWgui9MO5MIJGjgEae9NhTfDIBEkNHCaRtbm5GGbhHyY2BvK2tLbq5udGH66Qnog6dRcyxZtT0NhQc+X5Yfry9vS08+YkBMu4wYo4xDdHzAxGNyKrCgUiEzEEJlqj+EFeW5EpQmgxVYGQOE2lG2pMk0JOoVRhtHNo6tHkfHx+FF6HQJmKqHmcMXWJ2JOICsdj+8PCgzxAXAkhE5zI7OyuedC4iEJGF8R5SPUJJ68Vk8GMoZCYSpDojdoEQh6xRqTViky+Na0qIZBOIiMA2Bo6IK0KvSM4JBpZeGFF3dnYWTV4vEHl6esqWnx08ApELDYFlAwLR06+srFBIgkYgqkwZ5RnQyaBZCUkwgZCHjNCyg9zpkBKDCET1GAZ5BkgM1SZ6C4Q46d1BIQi1IcdLIHq4YZRnCLEhx0ug9Aaa0ECeb3voLBBjvBgbaEKDJHSfDTnOAqW2Ukng0ww5CUS1LcMWhlCgLK5R6CQw5vYtLlz3kzgJHKXoM7iOY60FQt4w97yDQI/sUo2tBZZh9xEXLrNH1gIl9uDGwqVpshY4itXX4FK7rAWWYZKUC5fgsBL4Ffa+2ZYxCfwPVoFl2PvGjW0ZrQVKf7SSFKZstgKtF5VwkVGV6EJKLvIkCfQEAo8o4cpJ1u12jynhykGmVuv3K5VKixJWwBn+KUGW53k7y7KdJLE4cAVn+nHvD/C5+qpK76lfSJ9u3gfl5kidjuv1+r5CLyr/AdUjKZcEN/rSAAAAAElFTkSuQmCC'
                  }
                />
              </div>
            </Col>
          </>
        );
      }}
    />
  );
}
